import { useCallback, useEffect, useState } from "react";

import {
  apiListCollaborators,
  apiRemoveCollaborator,
  apiUpdateCollaboratorPermission,
  apiListFolderCollaborators,
  apiRemoveFolderCollaborator,
  apiUpdateFolderCollaboratorPermission,
  type Collaborator,
  type Permission,
} from "../data/api";

import "./CollaboratorList.scss";

type Props = {
  /** Modo desenho: id do desenho cujos colaboradores serão listados. */
  drawingId?: string;
  /** Modo pasta: id da pasta cujos colaboradores serão listados. */
  folderId?: string;
  /** Muda quando um novo convite é enviado, para recarregar a lista. */
  reloadToken?: number;
  /** Oculta o título interno (quando o diálogo já tem um cabeçalho próprio). */
  hideHeader?: boolean;
};

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("pt-BR");
}

// Lista "Pessoas com acesso" exibida dentro dos diálogos de compartilhamento.
// Mostra quem tem acesso ao desenho agora e permite alterar a permissão ou
// revogar o acesso. Reaproveita os endpoints de colaboradores já existentes.
export const CollaboratorList = ({
  drawingId,
  folderId,
  reloadToken,
  hideHeader,
}: Props) => {
  const isFolder = folderId != null;
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = isFolder
        ? await apiListFolderCollaborators(folderId!)
        : await apiListCollaborators(drawingId!);
      setCollaborators(list as Collaborator[]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Falha ao carregar a lista.",
      );
    } finally {
      setLoading(false);
    }
  }, [drawingId, folderId, isFolder]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  const handlePermissionChange = async (
    userId: string,
    permission: Permission,
  ) => {
    setBusyId(userId);
    setError(null);
    try {
      if (isFolder) {
        await apiUpdateFolderCollaboratorPermission(
          folderId!,
          userId,
          permission as "view" | "edit",
        );
      } else {
        await apiUpdateCollaboratorPermission(drawingId!, userId, permission);
      }
      setCollaborators((prev) =>
        prev.map((c) => (c.user_id === userId ? { ...c, permission } : c)),
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Falha ao alterar a permissão.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (userId: string) => {
    setBusyId(userId);
    setError(null);
    try {
      if (isFolder) {
        await apiRemoveFolderCollaborator(folderId!, userId);
      } else {
        await apiRemoveCollaborator(drawingId!, userId);
      }
      setCollaborators((prev) => prev.filter((c) => c.user_id !== userId));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Falha ao remover o acesso.",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="CollaboratorList">
      {!hideHeader && (
        <div className="CollaboratorList__header">Pessoas com acesso</div>
      )}

      {loading && <div className="CollaboratorList__hint">Carregando...</div>}

      {!loading && collaborators.length === 0 && (
        <div className="CollaboratorList__hint">
          {isFolder
            ? "Ninguém tem acesso a esta pasta ainda."
            : "Ninguém tem acesso a este desenho ainda."}
        </div>
      )}

      {!loading && collaborators.length > 0 && (
        <ul className="CollaboratorList__items">
          {collaborators.map((c) => {
            const busy = busyId === c.user_id;
            return (
              <li key={c.user_id} className="CollaboratorList__item">
                <div className="CollaboratorList__person">
                  <span className="CollaboratorList__name">
                    {c.name || c.email}
                  </span>
                  <span className="CollaboratorList__meta">
                    {c.name ? `${c.email} · ` : ""}
                    Compartilhado em {formatDate(c.created_at)}
                  </span>
                </div>

                <select
                  className="CollaboratorList__perm"
                  value={c.permission}
                  disabled={busy}
                  onChange={(e) =>
                    handlePermissionChange(
                      c.user_id,
                      e.target.value as Permission,
                    )
                  }
                  aria-label={`Permissão de ${c.name || c.email}`}
                >
                  <option value="view">Visualizar</option>
                  <option value="edit">Editar</option>
                  {c.permission === "collab" && (
                    <option value="collab">Colaboração</option>
                  )}
                </select>

                <button
                  type="button"
                  className="CollaboratorList__remove"
                  disabled={busy}
                  onClick={() => handleRemove(c.user_id)}
                >
                  Remover acesso
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {error && <div className="CollaboratorList__error">{error}</div>}
    </div>
  );
};
